import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// JWT Parser (for client-side role extraction only)
export function parseJWT(token: string): any {
  if (!token || typeof token !== 'string') {
    console.error('Invalid token provided to parseJWT:', token);
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format - expected 3 parts, got:', parts.length);
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

// Form validation helpers
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

export const validateEmail = (email: string): boolean => emailRegex.test(email);
export const validatePassword = (password: string): boolean => passwordRegex.test(password);

// Date formatting
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// PKCE (Proof Key for Code Exchange) utilities
export async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hash));
  const hashBase64 = btoa(String.fromCharCode(...hashArray));
  return hashBase64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// OAuth2 state management
export function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export function storeOAuthState(state: string, verifier: string): void {
  const timestamp = Date.now().toString();
  const data = JSON.stringify({ state, verifier, timestamp });

  // Multiple storage strategies for different browser modes
  try {
    sessionStorage.setItem('oauth_state_data', data);
  } catch (e) {
    console.warn('sessionStorage not available:', e);
  }

  try {
    localStorage.setItem('oauth_state_data', data);
  } catch (e) {
    console.warn('localStorage not available:', e);
  }

  // Also store in URL hash as last resort (will be cleared after use)
  // Note: This is less secure but works in Brave private mode
  const hashData = btoa(data);
  window.location.hash = `oauth_state=${hashData}`;
}

export function getOAuthState(): { state: string; verifier: string } | null {
  const timestamp = Date.now();

  // Try multiple storage methods in order of preference

  // Method 1: sessionStorage (best for normal browsing)
  try {
    const data = sessionStorage.getItem('oauth_state_data');
    if (data) {
      const parsed = JSON.parse(data);
      const age = timestamp - parseInt(parsed.timestamp);
      if (age < 15 * 60 * 1000) {
        return { state: parsed.state, verifier: parsed.verifier };
      }
    }
  } catch (e) {
    // sessionStorage might be blocked
  }

  // Method 2: localStorage (fallback for private mode)
  try {
    const data = localStorage.getItem('oauth_state_data');
    if (data) {
      const parsed = JSON.parse(data);
      const age = timestamp - parseInt(parsed.timestamp);
      if (age < 15 * 60 * 1000) {
        return { state: parsed.state, verifier: parsed.verifier };
      }
    }
  } catch (e) {
    // localStorage might be blocked
  }

  // Method 3: URL hash (last resort for Brave private mode)
  // Only works if callback page reads it before hash is cleared
  try {
    const hash = window.location.hash;
    if (hash && hash.includes('oauth_state=')) {
      const match = hash.match(/oauth_state=([^&]+)/);
      if (match && match[1]) {
        const decoded = atob(match[1]);
        const parsed = JSON.parse(decoded);
        const age = timestamp - parseInt(parsed.timestamp);
        if (age < 15 * 60 * 1000) {
          // Clear the hash immediately for security
          window.location.hash = '';
          return { state: parsed.state, verifier: parsed.verifier };
        }
      }
    }
  } catch (e) {
    // Hash parsing might fail
  }

  return null;
}

export function clearOAuthState(): void {
  try {
    sessionStorage.removeItem('oauth_state_data');
  } catch (e) { }
  try {
    localStorage.removeItem('oauth_state_data');
  } catch (e) { }
  // Hash is already cleared by getOAuthState
}

// OAuth2 redirect URL builder
export function buildAuthorizeUrl(
  authorizeEndpoint: string,
  clientId: string,
  redirectUri: string,
  codeChallenge: string,
  state: string,
  scopes?: string
): string {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state: state,
  });

  if (scopes) {
    params.append('scope', scopes);
  }

  return `${authorizeEndpoint}?${params.toString()}`;
}

// OAuth2 logout URL builder (RP-initiated logout per OIDC spec)
export function buildLogoutUrl(
  logoutEndpoint: string,
  idTokenHint: string,
  postLogoutRedirectUri: string,
  state?: string
): string {
  const params = new URLSearchParams({
    id_token_hint: idTokenHint,
    post_logout_redirect_uri: postLogoutRedirectUri,
  });

  if (state) {
    params.append('state', state);
  }

  return `${logoutEndpoint}?${params.toString()}`;
}

import { API_BASE } from './env';

export function getOAuth2AuthorizeUrl(clientId: string, redirectUri: string, scopes?: string[]): string {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: scopes ? scopes.join(' ') : 'openid profile email',
  });
  return `${API_BASE}/oauth2/authorize?${params.toString()}`;
}

export function getOAuth2TokenUrl(): string {
  return `${API_BASE}/oauth2/token`;
}
